import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';

import { AcuityCategory } from 'src/shared/models/acuity-category.model';
import { AcuityCensusRecord } from 'src/shared/models/acuity-census-record.model';
import { PageInterface } from 'src/app/workflow/planner/page/page.interface';
import { Router } from '@angular/router';
import { AcuityShiftRecordService } from 'src/shared/services/shift-record-acuity.service';
import { ShiftRecordStatus } from 'src/shared/models/shift-record-status';
import { WorkflowPage } from 'src/shared/models/workflow-page.model';
import { WorkflowPageComponent } from 'src/app/workflow/base-classes/workflow-page/workflow-page.component';
import { WorkflowPageUtil } from '../../base-classes/workflow-page/workflow-page.util';
import { AcuityUtility } from '../acuity.util';
import { WorkflowUtilityService } from 'src/shared/services/workflow-utility.service';

@Component({
    selector: 'smt-review-previous',
    templateUrl: './review-previous.component.html',
    styleUrls: ['./review-previous.component.scss']
})
export class AcuityReviewPreviousComponent extends WorkflowPageComponent implements OnInit, PageInterface {
    @Input() page: WorkflowPage;
    @Output() previousRequested = new EventEmitter();

    public acuityCensusTotal = 0; 
    public acuityForm = new UntypedFormGroup({});
    public acuityFormValues$;
    public acuityCategories: AcuityCategory[]; 
    public formGroups: UntypedFormGroup[];
    public isEditing: boolean = false;
    private acuityCensusRecords: AcuityCensusRecord[] = [];

    get hasCensusRecords(): boolean {
        return this.acuityCensusRecords && this.acuityCensusRecords.length && this.acuityCensusRecords.length > 0;
    }

    constructor(private _router: Router, 
        private _acuityShiftRecordService: AcuityShiftRecordService,
        private _workflowUtilityService: WorkflowUtilityService,
        private _acuityUtil: AcuityUtility) {
        super(_router);        
    }

    ngOnInit() {
        super.initialize(this.page, () => this.initialize());
    }    

    getAcuityControlName(acuityId: number): string {
        return this._acuityUtil.getAcuityControlName(acuityId);
    }
    
    initialize() { 
      let costCenter = this.shiftRecord.costCenter;
      
        if (WorkflowPageUtil.hasAcuityConfiguration(costCenter)) {
            this.acuityCategories = this._workflowUtilityService.buildAcuityCategories(this.shiftRecord.prevShiftRecord);                        
        }           

        if (this.shiftRecord.prevShiftRecord) {
            this.acuityCensusRecords = this.shiftRecord.prevShiftRecord.acuityCensusRecords;
            this.acuityForm.addControl('reviewed', new UntypedFormControl(false, Validators.requiredTrue));
        }
        
        // Editing is only allowed when there is a completed previous shift record
        if (this.hasCensusRecords) {
            if (this.acuityCategories) {            
                this.acuityCategories.forEach(a => {
                    let controlName = this.getAcuityControlName(a.id);
                    this.acuityForm.addControl(controlName, new UntypedFormControl(null, [Validators.required, Validators.min(0)]));
                });
            }
            this.acuityCensusRecords.forEach(acuityCensusRecord => {
                this.acuityForm.get(this.getAcuityControlName(acuityCensusRecord.acuityCategoryId)).setValue(acuityCensusRecord.patients);
            });
            this.acuityCensusTotal = this._acuityUtil.getCensusTotal(this.acuityForm.value, this.shiftRecord.id, this.acuityCategories, this.acuityCensusRecords);
            
            this.acuityFormValues$ = this.acuityForm.valueChanges.subscribe(async values => {
                this.acuityCensusTotal = this._acuityUtil.getCensusTotal(values, this.shiftRecord.id, this.acuityCategories, this.acuityCensusRecords);
            });
        }
        
        this.formGroups = [ this.acuityForm ];
    }

    valueOutOfRange(acuityCategoryId: number) : boolean {
        return this._acuityUtil.valueOutOfRange(acuityCategoryId, this.acuityForm);
    }

    isFormValid(): boolean {
        return this.acuityForm && this.acuityForm.valid;
    }

    toggleIsEditing() {
        this.isEditing = !this.isEditing;
        if (this.isEditing) {
            let control = this.acuityForm.get('reviewed');
            control.setValue(false);
            control.markAsUntouched();
            control.markAsPristine();
        }
    }

    async save(): Promise<number> {
        this.setStatus();
        this.setAcuityCensus();
        if (!this.shiftRecord.prevShiftRecord) {
            return this._acuityShiftRecordService.setShiftRecordWithAssociated(this.shiftRecord);
        }

        return await this._acuityShiftRecordService.setShiftRecordWithAssociated(this.shiftRecord.prevShiftRecord).then((prevShiftRecordId) => {
            this.shiftRecord.prevShiftRecordId = prevShiftRecordId;
            return this._acuityShiftRecordService.setShiftRecordWithAssociated(this.shiftRecord);
        }, 
        (errors) => { console.error(errors); return 0; });
    }

    private setStatus() {
        if (this.shiftRecord.status !== ShiftRecordStatus.Done && this.page.nextPage) {
            this.shiftRecord.status = this.page.nextPage.completedStatus;
        }
    }

    private setAcuityCensus() {     
        if (this.shiftRecord.prevShiftRecord)    {
            this.shiftRecord.prevShiftRecord.acuityCensusRecords = this.acuityCensusRecords;
        }        
    }

    shouldSkipSave(): boolean {
        // Find the reviewed toggle from the form.
        let control = this.acuityForm.get('reviewed');

        if (control) {
            // Ignore the reviewed toggle when determining if the form is dirty.
            control.markAsUntouched();
            control.markAsPristine();
        }        

        // Only actually save if the form is dirty once the reviewed toggle is ignored.
        return this.isFormValid() && !this.acuityForm.dirty;
    }
}
